/* The tool funnel, reported to GA4.
 *
 * ONE DOOR, for the same reason addFiles() is one door. Every gtag() call on
 * this site goes through track() below, and tests/analytics_test.mjs fails if
 * a second one appears anywhere in src/. Scattering gtag() through components
 * is how an event comes to be sent from a page that has no tag, or sent twice
 * from a component that re-renders, or sent with a filename in it because the
 * component that had the filename to hand was the convenient place to put the
 * call.
 *
 * WHAT MAY BE SENT: formats, counts, byte sizes, durations, and the slug of
 * the page. Nothing derived from the CONTENTS of anybody's picture -- no
 * filename, no EXIF, no dimensions, no colours. scrub() drops any key not on
 * the allow-list below rather than trusting every caller to remember, because
 * a filename is exactly what somebody adds "just for debugging" in a hurry.
 *
 * FAIL SILENT, ALWAYS. gtag is absent when the library is blocked, when an
 * extension removes it, on a page built before the tag existed, and in every
 * test harness. None of those is a reason for the tool to stop working or to
 * print anything at the visitor. track() returns false and the caller does not
 * check.
 *
 * NO READINESS CHECK NEEDED, and that is by construction rather than luck.
 * The snippet in index.html defines window.gtag INLINE, before the async
 * library is fetched, and it pushes to window.dataLayer. So a call made in the
 * first second of the page queues rather than being lost, and a call made when
 * the library never arrives grows an array nobody reads. Either way there is
 * nothing to wait for and nothing to fail.
 */

import { activeTask } from "./task"

/* GA4's own limits, which are not negotiable and are silently enforced at
 * their end: a parameter name may be 40 characters and a value 100. Something
 * over the line is not rejected loudly -- it is truncated or dropped, and the
 * report then has a hole in it that looks like missing traffic. Held here so
 * the failure is ours to see rather than theirs to swallow. */
const MAX_NAME = 40
const MAX_VALUE = 100

/* THE ALLOW-LIST IS THE PRIVACY CONTROL.
 *
 * Not a convention, not a code review habit: a key absent from here does not
 * reach Google, whatever a caller passes. Adding a key is a deliberate act and
 * the test names the ones that may never be added -- filename, name, path,
 * exif, gps, lat, lon, width, height, title, alt.
 */
const ALLOWED = new Set([
    "tool",              // the task slug: which landing page this happened on
    "entry_method",      // how the file arrived
    "file_count",
    "input_format",
    "output_format",
    "total_input_bytes",
    "total_output_bytes",
    "duration_ms",
    "picture_element",
    "download_type",
    "error_type",
])

/* A format is a short lower-case token, never a MIME type and never an
 * extension off a filename.
 *
 * "image/jpeg" and ".JPG" and "jpeg" are the same format written three ways,
 * and a report that splits them three ways is worth nothing. The mapping is
 * here so every call site answers the question identically.
 *
 * A file's own name is deliberately NOT consulted. type is the OS's extension
 * map and goes missing often enough to matter -- see namedByAttr() in task.js
 * -- and the honest answer when it is missing is "unknown", not a substring of
 * something somebody typed.
 */
const FORMAT_FROM_MIME = {
    "image/jpeg": "jpeg",
    "image/jpg": "jpeg",
    "image/png": "png",
    "image/webp": "webp",
    "image/avif": "avif",
    "image/gif": "gif",
    "image/jxl": "jxl",
    "image/heic": "heic",
    "image/heif": "heic",
    "image/svg+xml": "svg",
    "image/bmp": "bmp",
    "application/pdf": "pdf",
    "video/mp4": "mp4",
    "video/quicktime": "mov",
    "video/x-m4v": "mp4",
    "video/webm": "webm",
    "video/ogg": "ogv",
    "video/3gpp": "3gp",
    "video/x-matroska": "mkv",
    "video/x-msvideo": "avi",
}

export function formatOf(file) {
    if (!file) return "unknown"
    const type = String(file.type || "").toLowerCase().split(";")[0].trim()
    if (FORMAT_FROM_MIME[type]) return FORMAT_FROM_MIME[type]
    /* A type we have no name for still tells us the family, which is the part
     * worth reporting: "video" on a picture page is a refusal worth counting. */
    if (type.startsWith("video/")) return "video"
    if (type.startsWith("image/")) return "image"
    return "unknown"
}

/* Several files, one answer.
 *
 * A mixed drop is normal -- twelve photographs and a stray PNG -- and
 * reporting only the first file's format would quietly misdescribe it. One
 * format when they agree, "mixed" when they do not. */
export function formatOfAll(files) {
    const seen = new Set([...(files || [])].map(formatOf))
    if (!seen.size) return "unknown"
    if (seen.size === 1) return [...seen][0]
    return "mixed"
}

export function totalBytes(files) {
    return [...(files || [])].reduce((sum, f) => sum + (Number(f && f.size) || 0), 0)
}

/* Drop what may not be sent, and make what is left fit.
 *
 * Numbers are rounded because GA4 stores a float as a float and a byte count
 * with a decimal point in it reads as a bug in the report. A boolean goes as
 * true/false rather than 1/0 so the dimension reads the way the parameter is
 * named. Anything else becomes a string and is cut to length.
 */
function scrub(params) {
    const out = {}
    for (const key of Object.keys(params || {})) {
        if (!ALLOWED.has(key)) continue
        if (key.length > MAX_NAME) continue
        const value = params[key]
        if (value === undefined || value === null) continue
        if (typeof value === "number") {
            if (!Number.isFinite(value)) continue
            out[key] = Math.round(value)
        } else if (typeof value === "boolean") {
            out[key] = value
        } else {
            const text = String(value)
            if (!text) continue
            out[key] = text.length > MAX_VALUE ? text.slice(0, MAX_VALUE) : text
        }
    }
    return out
}

/* Which page this happened on.
 *
 * The whole point of the exercise is a funnel PER LANDING PAGE, and GA4's own
 * page dimension would do it -- except that a task page and the home page are
 * the same application and the report is easier to read when the tool names
 * itself. "home" rather than an empty string, because a blank value in a GA
 * report is indistinguishable from a broken one.
 */
function toolName() {
    const task = activeTask()
    return (task && task.slug) || "home"
}

export function track(name, params) {
    try {
        const gtag = typeof window !== "undefined" && window.gtag
        if (typeof gtag !== "function") return false
        gtag("event", name, Object.assign({tool: toolName()}, scrub(params)))
        return true
    } catch (err) {
        /* An analytics call may not take the tool down with it. There is
         * deliberately nothing in here -- not a console.warn either, because
         * a blocked tag would then print on every interaction of every
         * session, which is noise in somebody else's bug report. */
        return false
    }
}

/* Fire once per subject, however many times the caller asks.
 *
 * Vue re-renders, a settings change re-runs the whole optimisation, and a
 * visitor presses a button twice when the first press seems slow. All three
 * produce a second call that is not a second event. The key is whatever
 * identifies the subject -- a staged file's id, a run counter -- and the
 * caller owns choosing it; this only remembers.
 *
 * A Set rather than a flag because several subjects are live at once: twenty
 * batch rows can each finish once.
 */
const fired = new Set()

export function trackOnce(key, name, params) {
    if (!key || fired.has(key)) return false
    fired.add(key)
    return track(name, params)
}

/* A new intake clears the slate.
 *
 * Staging a second picture is a second funnel and must be able to report the
 * same events again. Called from addFiles() beside the other module-scope
 * state it resets -- lastClip, lastDrawing -- for the same reason they are
 * there: $reset() wipes the store, and this does not live in the store.
 */
export function resetFunnel() {
    fired.clear()
}
